import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLogto } from "@logto/react";
import { useAuthStore } from "@/stores/auth-store";
import {
  setLogtoTokenProvider,
  clearLogtoTokenProvider,
} from "@/services/auth/token-provider";

/**
 * Domain-to-Organization mapping.
 * VITE_ORG_ID env var takes precedence over domain mapping.
 */
const DOMAIN_ORG_MAP: Record<string, string> = {
  "admin.lexe.pro": "vij1h45hy26m",
  "admin.stage.lexe.pro": "lexe-default-stage",
  localhost: "lexe-default-stage",
};

function getOrganizationId(): string | undefined {
  const envOrg = import.meta.env.VITE_ORG_ID;
  if (envOrg) return envOrg;
  return DOMAIN_ORG_MAP[window.location.hostname];
}

interface LogtoAdminContextType {
  getAccessToken: (resource?: string) => Promise<string | undefined>;
  signOut: (redirectUri?: string) => Promise<void>;
  signIn: (redirectUri: string) => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  currentOrganizationId: string | undefined;
}

const LogtoAdminContext = createContext<LogtoAdminContextType>({
  getAccessToken: async () => undefined,
  signOut: async () => {},
  signIn: async () => {},
  isAuthenticated: false,
  isLoading: true,
  currentOrganizationId: undefined,
});

export function LogtoAdminProvider({ children }: { children: ReactNode }) {
  const logto = useLogto();
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | undefined>(
    getOrganizationId(),
  );

  useEffect(() => {
    const syncUser = async () => {
      if (!logto.isAuthenticated) {
        useAuthStore.getState().setLoading(false);
        return;
      }

      try {
        const claims = await logto.getIdTokenClaims();
        const userOrgs = claims?.organizations ?? [];
        const domainOrg = getOrganizationId();

        if (domainOrg && userOrgs.includes(domainOrg)) {
          setCurrentOrganizationId(domainOrg);
        } else if (userOrgs.length > 0) {
          setCurrentOrganizationId(userOrgs[0]);
        }

        if (claims) {
          setLogtoTokenProvider(getAccessToken);
          useAuthStore.getState().setAuthenticated({
            sub: claims.sub,
            email: claims.email ?? undefined,
            name: claims.name ?? claims.username ?? claims.email?.split("@")[0],
          });
        }
      } catch (err) {
        console.error("[LogtoAdmin] Failed to get ID token claims:", err);
        useAuthStore.getState().setLoading(false);
      }
    };

    syncUser();
  }, [logto.isAuthenticated]);

  const getAccessToken = useCallback(
    async (resource?: string): Promise<string | undefined> => {
      if (!logto.isAuthenticated) return undefined;

      const apiResource = resource || import.meta.env.VITE_LOGTO_RESOURCE || undefined;

      try {
        if (currentOrganizationId && apiResource) {
          const token = await logto.getAccessToken(apiResource, currentOrganizationId);
          if (token) return token;
        }
        const token = await logto.getAccessToken(apiResource);
        return token ?? undefined;
      } catch (err) {
        console.error("[LogtoAdmin] Failed to get token:", err);
        return undefined;
      }
    },
    [logto.isAuthenticated, logto.getAccessToken, currentOrganizationId],
  );

  useEffect(() => {
    setLogtoTokenProvider(getAccessToken);
    return () => clearLogtoTokenProvider();
  }, [getAccessToken]);

  const value = useMemo(
    () => ({
      getAccessToken,
      signOut: logto.signOut,
      signIn: logto.signIn,
      isAuthenticated: logto.isAuthenticated,
      isLoading: logto.isLoading,
      currentOrganizationId,
    }),
    [getAccessToken, logto.signOut, logto.signIn, logto.isAuthenticated, logto.isLoading, currentOrganizationId],
  );

  return <LogtoAdminContext.Provider value={value}>{children}</LogtoAdminContext.Provider>;
}

export function useLogtoAdmin(): LogtoAdminContextType {
  return useContext(LogtoAdminContext);
}
